import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ExamHeader from "../../components/exam-header/exam-header.component";
import ExamFooter from "../../components/exam-footer/exam-footer.component";
import ExamReading from "../../components/exam-reading/exam-reading.component";
import ExamListening from "../../components/exam-listening/exam-listening.component";
import ExamWriting from "../../components/exam-writing/exam-writing.component";
import {
  Box,
  styled,
  CircularProgress,
  Button,
  Typography,
} from "@mui/material";
import { MAPPING_EXAM_DURATION, SectionType } from "@app/constants";
import { Test } from "@app/models/test.model";
import { Section } from "@app/models/section.model";
import TestService from "@core/services/http/test.service";
import { AnswerRequest } from "@app/types/answer.type";
import useObservable from "@core/hooks/use-observable.hook";
import ExamAttemptService from "@core/services/http/exam-attemp.service";
import { useSelector } from "react-redux";
import { HIGHLIGHT_STORAGE_KEY } from "@app/utils/highlight.utils";

interface MainContentProps {
  footerHeight: number;
}

const ExamContainer = styled(Box)({
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#f5f5f5",
  overflow: "hidden",
});

const LoadingContainer = styled(Box)({
  height: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const MainContent = styled(Box)<MainContentProps>(
  ({ theme, footerHeight }) => ({
    flex: 1,
    marginTop: "64px",
    marginBottom: `${footerHeight}px`,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: "16px",
      marginTop: "56px",
      marginBottom: "56px",
    },
  })
);

function ExamPage() {
  const { subscribeOnce } = useObservable();
  const footerRef = useRef<HTMLDivElement>(null);
  const params = useParams();
  const navigate = useNavigate();
  const testId = params.id;
  const [currentTest, setCurrentTest] = useState<Test | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPart, setCurrentPart] = useState<SectionType>(
    SectionType.LISTENING
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [nextPart, setNextPart] = useState<SectionType | null>(null);
  const [footerHeight, setFooterHeight] = useState(64);
  const [answeredQuestionValue, setAnsweredValue] = useState<any>(null);
  const [answeredQuestionId, setAnsweredQuestionId] = useState<any>(null);
  // const [isSubmittedWriting, setIsSubmittedWriting] = useState<boolean>(false);
  const [childSelectedAnswers, setChildSelectedAnswers] = useState<
    Record<number, AnswerRequest>
  >({});
  const childSelectedAnswersRef = useRef({});
  const currentAttemptId = useSelector(
    (state: any) => state.examAttempt.currentAttemptId
  );

  useEffect(() => {
    const updateFooterHeight = () => {
      if (footerRef.current) {
        const height = footerRef.current.offsetHeight;

        setFooterHeight(height);
      }
    };

    const timer = setTimeout(() => {
      updateFooterHeight();
    }, 100);

    window.addEventListener("resize", updateFooterHeight);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateFooterHeight);
    };
  }, [currentPart]);

  useEffect(() => {
    if (!testId) {
      setIsLoading(false);

      return;
    }

    const loadTestData = async () => {
      try {
        setIsLoading(true);
        const testResponse = await TestService.getDetailTest(
          testId
        ).toPromise();

        setCurrentTest(testResponse as Test);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error loading exam data:", error);
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    };

    loadTestData();
  }, [testId, navigate]);

  useEffect(() => {
    if (currentTest) {
      setCurrentQuestion(1);
      setCurrentPart(SectionType.LISTENING);
      setIsReviewMode(false);

      if (footerRef.current) {
        const height = footerRef.current.offsetHeight;

        setFooterHeight(height);
      }
    }
  }, [currentTest]);

  useEffect(() => {
    childSelectedAnswersRef.current = childSelectedAnswers;
  }, [childSelectedAnswers]);

  useEffect(() => {
    localStorage.setItem(HIGHLIGHT_STORAGE_KEY, "");
    // const handleBeforeUnload = () => {
    //   localStorage.setItem(HIGHLIGHT_STORAGE_KEY, "");
    // };

    // window.addEventListener("beforeunload", handleBeforeUnload);

    // return () => {
    //   window.removeEventListener("beforeunload", handleBeforeUnload);
    // };
  }, []);

  const handleSelectedAnswersChange = (
    newSelectedAnswers: Record<number, AnswerRequest>
  ) => {
    setChildSelectedAnswers(newSelectedAnswers);
  };

  const handleReview = (isReview: boolean) => {
    setIsReviewMode(isReview);
  };

  const questionsBySectionType: Record<SectionType, Section[]> =
    currentTest?.sections?.reduce((acc, section) => {
      const sectionType = section.type;

      if (!acc[sectionType]) {
        acc[sectionType] = [];
      }

      acc[sectionType].push(section);

      return acc;
    }, {} as Record<SectionType, Section[]>) || {
      [SectionType.READING]: [],
      [SectionType.LISTENING]: [],
      [SectionType.WRITING]: [],
    };

  const handlePartSubmit = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscribeOnce(
      ExamAttemptService.completeSection(currentAttemptId, currentPart),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (res) => {
        localStorage.setItem(HIGHLIGHT_STORAGE_KEY, "");

        if (currentPart !== SectionType.WRITING) {
          setNextPart(getNextPart(currentPart));
          setCurrentPart(getNextPart(currentPart) as SectionType);
          setCurrentQuestion(1);
          setAnsweredValue(null);
          setAnsweredQuestionId(null);
          setChildSelectedAnswers({});
        } else {
          navigate("/exam-result");
        }
      }
    );
    subscribeOnce(
      ExamAttemptService.sendAnswers(
        currentAttemptId,
        Object.values(childSelectedAnswersRef.current)
      ),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (res) => {}
    );
  };

  const handleChangeAnswer = (
    value: string | string[] | Record<number, string>,
    questionId: number
  ) => {
    setAnsweredValue(value);
    setAnsweredQuestionId(questionId);
  };

  const getNextPart = (currentPart: SectionType): SectionType | null => {
    switch (currentPart) {
      case SectionType.LISTENING:
        return SectionType.READING;
      case SectionType.READING:
        return SectionType.WRITING;
      case SectionType.WRITING:
        return null;
      default:
        return null;
    }
  };

  const handleChangeQuestion = (questionNumber: number) => {
    setCurrentQuestion(questionNumber);
  };

  const renderTestContent = () => {
    switch (currentPart) {
      case SectionType.READING:
        return (
          <ExamReading
            testId={currentTest?.id as number}
            currentQuestion={currentQuestion}
            questionsBySection={
              questionsBySectionType[SectionType.READING] || []
            }
            onChangeAnswer={handleChangeAnswer}
            onQuestionChange={handleChangeQuestion}
            onSelectedAnswersChange={handleSelectedAnswersChange}
          />
        );
      case SectionType.LISTENING:
        return (
          <ExamListening
            testId={currentTest?.id as number}
            currentQuestion={currentQuestion}
            questionsBySection={
              questionsBySectionType[SectionType.LISTENING] || []
            }
            onChangeAnswer={handleChangeAnswer}
            onQuestionChange={handleChangeQuestion}
            onSelectedAnswersChange={handleSelectedAnswersChange}
          />
        );
      case SectionType.WRITING:
        return (
          <ExamWriting
            testId={currentTest?.id as number}
            currentQuestion={currentQuestion}
            questionsBySection={
              questionsBySectionType[SectionType.WRITING] || []
            }
            onChangeAnswer={handleChangeAnswer}
            // isSubmittedWriting={isSubmittedWriting}
            onSelectedAnswersChange={handleSelectedAnswersChange}
          />
        );
      default:
        return <div>Invalid exam type</div>;
    }
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  if (!currentTest) {
    return (
      <LoadingContainer>
        <Typography>No exam data available</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/")}
          sx={{ mt: 2 }}
        >
          Return to Home
        </Button>
      </LoadingContainer>
    );
  }

  return (
    <ExamContainer>
      <ExamHeader
        duration={MAPPING_EXAM_DURATION[currentPart].toString()}
        sectionType={currentPart}
        onSubmit={() => handlePartSubmit()}
      />
      <MainContent footerHeight={footerHeight}>
        {renderTestContent()}
      </MainContent>
      <ExamFooter
        ref={footerRef}
        currentQuestion={currentQuestion}
        onQuestionChange={handleChangeQuestion}
        onReview={handleReview}
        questionsBySectionType={questionsBySectionType[currentPart] || []}
        answeredQuestionId={answeredQuestionId}
        answeredQuestionValue={answeredQuestionValue}
        sectionType={currentPart}
      />
    </ExamContainer>
  );
}

export default ExamPage;
